/**
 * useAnalytics Hook
 *
 * Provides a convenient interface for tracking analytics events.
 * All methods are memoized and respect the analytics feature flag.
 */

import { useCallback } from 'react';
import { featureFlags } from '../lib/featureFlags';
import {
  trackStagingCompleted,
  trackSignUp,
  trackLogin,
  trackSubscriptionCreated,
  trackSubscriptionUpgraded,
  trackProjectCreated,
  trackImageUploaded,
  trackImageDownloaded,
  trackStyleSelected,
  trackPageView,
} from '../lib/analytics';

export function useAnalytics() {
  const isEnabled = featureFlags.analytics;

  // Tier 1 - North Star
  const stagingCompleted = useCallback(
    (params: Parameters<typeof trackStagingCompleted>[0]) => {
      if (isEnabled) trackStagingCompleted(params);
    },
    [isEnabled]
  );

  // Tier 2 - Funnel
  const signUp = useCallback(
    (params: Parameters<typeof trackSignUp>[0]) => {
      if (isEnabled) trackSignUp(params);
    },
    [isEnabled]
  );

  const login = useCallback(
    (params: Parameters<typeof trackLogin>[0]) => {
      if (isEnabled) trackLogin(params);
    },
    [isEnabled]
  );

  const subscriptionCreated = useCallback(
    (params: Parameters<typeof trackSubscriptionCreated>[0]) => {
      if (isEnabled) trackSubscriptionCreated(params);
    },
    [isEnabled]
  );

  const subscriptionUpgraded = useCallback(
    (params: Parameters<typeof trackSubscriptionUpgraded>[0]) => {
      if (isEnabled) trackSubscriptionUpgraded(params);
    },
    [isEnabled]
  );

  // Tier 3 - Engagement
  const projectCreated = useCallback(
    (params: Parameters<typeof trackProjectCreated>[0]) => {
      if (isEnabled) trackProjectCreated(params);
    },
    [isEnabled]
  );

  const imageUploaded = useCallback(
    (params: Parameters<typeof trackImageUploaded>[0]) => {
      if (isEnabled) trackImageUploaded(params);
    },
    [isEnabled]
  );

  const imageDownloaded = useCallback(
    (params: Parameters<typeof trackImageDownloaded>[0]) => {
      if (isEnabled) trackImageDownloaded(params);
    },
    [isEnabled]
  );

  const styleSelected = useCallback(
    (params: Parameters<typeof trackStyleSelected>[0]) => {
      if (isEnabled) trackStyleSelected(params);
    },
    [isEnabled]
  );

  const pageView = useCallback(
    (params: Parameters<typeof trackPageView>[0]) => {
      if (isEnabled) trackPageView(params);
    },
    [isEnabled]
  );

  return {
    isEnabled,
    // Tier 1 - North Star
    stagingCompleted,
    // Tier 2 - Funnel
    signUp,
    login,
    subscriptionCreated,
    subscriptionUpgraded,
    // Tier 3 - Engagement
    projectCreated,
    imageUploaded,
    imageDownloaded,
    styleSelected,
    pageView,
  };
}
